import { FONTS } from '../../../config/app-config';
import { getResponseErrorMessage as responseError } from '../../../utils/apiErrors';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';

import { secureFetch } from '../../../utils/secureFetch';

export const JOB_STATUS = {
    PENDING: 'PENDING',
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    STOPPED: 'STOPPED',
};

export const EXIT_STATUSES = new Set([
    JOB_STATUS.COMPLETED,
    JOB_STATUS.FAILED,
    JOB_STATUS.STOPPED,
]);

const normaliseStatus = (status) => String(status || JOB_STATUS.PENDING).trim().toUpperCase();

const normaliseJob = (job) => ({
    ...job,
    jobId: job.jobId ?? job.job_id,
    groupId: job.groupId ?? job.group_id ?? null,
    status: normaliseStatus(job.status),
    progress: Number(job.progress) || 0,
});

export const determineGroupStatus = (jobs) => {
    const statuses = jobs.map((job) => normaliseStatus(job.status));
    if (statuses.length === 0 || statuses.every((status) => status === JOB_STATUS.PENDING)) {
        return JOB_STATUS.PENDING;
    }

    if (statuses.some((status) => !EXIT_STATUSES.has(status))) {
        return JOB_STATUS.RUNNING;
    }

    if (statuses.includes(JOB_STATUS.FAILED)) return JOB_STATUS.FAILED;
    if (statuses.includes(JOB_STATUS.STOPPED)) return JOB_STATUS.STOPPED;
    return JOB_STATUS.COMPLETED;
};

export const determineGroupProgress = (jobs) => {
    const reportedProgress = jobs
        .map((job) => Number(job.progress))
        .filter((progress) => Number.isFinite(progress) && progress > 0);

    if (reportedProgress.length === 0) return 0;
    const average = reportedProgress.reduce((sum, progress) => sum + progress, 0)
        / reportedProgress.length;
    return Math.min(100, Math.max(0, average));
};

export const groupJobs = (jobs) => {
    const items = [];
    const groups = new Map();

    jobs.map(normaliseJob).forEach((job) => {
        if (!job.jobId) return;

        if (!job.groupId) {
            items.push({ type: 'job', id: job.jobId, jobs: [job] });
            return;
        }

        if (!groups.has(job.groupId)) {
            const group = { type: 'group', id: job.groupId, jobs: [] };
            groups.set(job.groupId, group);
            items.push(group);
        }
        groups.get(job.groupId).jobs.push(job);
    });

    return items;
};

const statusColour = {
    [JOB_STATUS.PENDING]: 'default',
    [JOB_STATUS.RUNNING]: 'info',
    [JOB_STATUS.COMPLETED]: 'success',
    [JOB_STATUS.FAILED]: 'error',
    [JOB_STATUS.STOPPED]: 'warning',
};

const displayStatus = (status) => status.charAt(0) + status.slice(1).toLowerCase();

const formatProgress = (progress) => (
    Number.isInteger(progress) ? String(progress) : progress.toFixed(1)
);


function ProgressDisplay({ value }) {
    return (
        <Box sx={{ minWidth: 150 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Progress</Typography>
                <Typography variant="body2" fontWeight={600}>{formatProgress(value)}%</Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={value}
                aria-label={`Progress ${formatProgress(value)} percent`}
                sx={{ height: 7, borderRadius: 4 }}
            />
        </Box>
    );
}

export default function JobManagement({
    open,
    isAuthenticated,
    login,
    adminApiBase,
}) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [livePolling, setLivePolling] = useState(false);
    const [pollingSeconds, setPollingSeconds] = useState(5);
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    const [stopping, setStopping] = useState(() => new Set());
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
    const requestInFlight = useRef(false);

    const notify = useCallback((message, severity = 'success') => {
        setSnack({ open: true, message, severity });
    }, []);

    const fetchJobs = useCallback(async ({ background = false } = {}) => {
        if (requestInFlight.current) return;
        requestInFlight.current = true;
        background ? setRefreshing(true) : setLoading(true);

        try {
            const response = await secureFetch(`${adminApiBase}jobs/info`, {}, true);
            if (!response.ok) {
                throw new Error(await responseError(response, 'Failed to load jobs.'));
            }

            const payload = await response.json();
            const nextJobs = Array.isArray(payload) ? payload : payload?.jobs;
            if (!Array.isArray(nextJobs)) {
                throw new Error('The jobs API returned an unexpected response.');
            }
            setJobs(nextJobs);
        } catch (error) {
            console.error('Failed to load jobs', error);
            notify(error.message || 'Failed to load jobs.', 'error');
        } finally {
            requestInFlight.current = false;
            setLoading(false);
            setRefreshing(false);
        }
    }, [adminApiBase, notify]);

    useEffect(() => {
        if (!open) return;
        if (!isAuthenticated) {
            login();
            return;
        }
        fetchJobs();
    }, [open, isAuthenticated, login, fetchJobs]);

    useEffect(() => {
        if (!open || !isAuthenticated || !livePolling) return undefined;

        const interval = window.setInterval(() => {
            fetchJobs({ background: true });
        }, pollingSeconds * 1000);

        return () => window.clearInterval(interval);
    }, [open, isAuthenticated, livePolling, pollingSeconds, fetchJobs]);

    const groupedJobs = useMemo(() => groupJobs(jobs), [jobs]);

    const toggleGroup = (groupId) => {
        setExpandedGroups((current) => {
            const next = new Set(current);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    const stopItem = async (item) => {
        const requestKey = `${item.type}:${item.id}`;
        const url = item.type === 'group'
            ? `${adminApiBase}jobs/groups/${item.id}/stop`
            : `${adminApiBase}jobs/${item.id}/stop`;

        setStopping((current) => new Set(current).add(requestKey));
        try {
            const response = await secureFetch(url, { method: 'POST' }, true);
            if (!response.ok) {
                throw new Error(await responseError(response, `Failed to stop ${item.type}.`));
            }
            notify(`${item.type === 'group' ? 'Job group' : 'Job'} stop requested.`);
            await fetchJobs({ background: true });
        } catch (error) {
            console.error(`Failed to stop ${item.type}`, error);
            notify(error.message || `Failed to stop ${item.type}.`, 'error');
        } finally {
            setStopping((current) => {
                const next = new Set(current);
                next.delete(requestKey);
                return next;
            });
        }
    };

    const updatePollingSeconds = (event) => {
        const value = Number(event.target.value);
        if (Number.isFinite(value)) setPollingSeconds(Math.max(1, Math.floor(value)));
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
                variant="h6"
                sx={{ fontFamily: FONTS.heading, fontWeight: 700, mt: 2 }}
            >
                Manage Jobs
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    my: 2,
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={() => fetchJobs({ background: true })}
                    disabled={loading || refreshing}
                    sx={{color: 'accent.main', borderColor: 'accent.main'}}
                >
                    Refresh
                </Button>
                <FormControlLabel
                    control={(
                        <Switch
                            checked={livePolling}
                            onChange={(event) => setLivePolling(event.target.checked)}
                            inputProps={{ 'aria-label': 'Enable live polling' }}
                            sx={{
                                '& .MuiSwitch-switchBase': {
                                    '&.Mui-checked': {
                                        color: 'accent.main',
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: 'accent.main',
                                        },
                                    },
                                },
                            }}
                        />
                    )}
                    label="Live polling"
                />
                <TextField
                    label="Polling interval (seconds)"
                    type="number"
                    size="small"
                    value={pollingSeconds}
                    onChange={updatePollingSeconds}
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ width: 205 }}
                />
                {livePolling && (
                    <Typography variant="body2" color="text.secondary">
                        Refreshing every {pollingSeconds} second{pollingSeconds === 1 ? '' : 's'}
                    </Typography>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : groupedJobs.length === 0 ? (
                <Alert severity="info">No jobs are currently available.</Alert>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>ID</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Status</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Progress</TableCell>
                                <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedJobs.map((item) => {
                                const isGroup = item.type === 'group';
                                const status = isGroup
                                    ? determineGroupStatus(item.jobs)
                                    : item.jobs[0].status;
                                const progress = isGroup
                                    ? determineGroupProgress(item.jobs)
                                    : Math.min(100, Math.max(0, item.jobs[0].progress));
                                const isExpanded = isGroup && expandedGroups.has(item.id);
                                const requestKey = `${item.type}:${item.id}`;
                                const isStopping = stopping.has(requestKey);
                                const canStop = !EXIT_STATUSES.has(status);

                                return (
                                    <React.Fragment key={requestKey}>
                                        <TableRow hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 220 }}>
                                                    {isGroup ? (
                                                        <Tooltip title={isExpanded ? 'Collapse group' : 'Expand group'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleGroup(item.id)}
                                                                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} job group ${item.id}`}
                                                            >
                                                                {isExpanded
                                                                    ? <KeyboardArrowUpIcon />
                                                                    : <KeyboardArrowDownIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Box sx={{ width: 34 }} />
                                                    )}
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                            {item.id}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {isGroup ? `Group · ${item.jobs.length} jobs` : 'Single job'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={displayStatus(status)}
                                                    color={statusColour[status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell><ProgressDisplay value={progress} /></TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    startIcon={isStopping
                                                        ? <CircularProgress size={14} color="inherit" />
                                                        : <StopCircleOutlinedIcon />}
                                                    onClick={() => stopItem(item)}
                                                    disabled={!canStop || isStopping}
                                                    aria-label={`Stop ${isGroup ? 'job group' : 'job'} ${item.id}`}
                                                >
                                                    Stop
                                                </Button>
                                            </TableCell>
                                        </TableRow>

                                        {isGroup && (
                                            <TableRow>
                                                <TableCell sx={{ py: 0 }} colSpan={4}>
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{ py: 1.5, pl: 5 }}>
                                                            {item.jobs.map((job) => (
                                                                <Typography
                                                                    key={job.jobId}
                                                                    variant="body2"
                                                                    sx={{ fontFamily: 'monospace', py: 0.25 }}
                                                                >
                                                                    {job.jobId}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((current) => ({ ...current, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snack.severity}
                    variant="filled"
                    onClose={() => setSnack((current) => ({ ...current, open: false }))}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
